import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { translations, getCurrentLanguage, getCurrentSearchCountry, setLanguage as setLang, setSearchCountry as setCountry } from '../utils/i18n';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create context
const LanguageContext = createContext(null);

// Available languages
export const LANGUAGES = [
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

// Available search countries
export const SEARCH_COUNTRIES = [
  { code: 'DK', name: 'Danmark', flag: '🇩🇰', nameKey: 'denmark' },
  { code: 'DE', name: 'Tyskland', flag: '🇩🇪', nameKey: 'germany' },
  { code: 'SE', name: 'Sverige', flag: '🇸🇪', nameKey: 'sweden' },
  { code: 'NO', name: 'Norge', flag: '🇳🇴', nameKey: 'norway' },
  { code: 'NL', name: 'Nederland', flag: '🇳🇱', nameKey: 'netherlands' },
  { code: 'GB', name: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', nameKey: 'england' },
  { code: 'ES', name: 'España', flag: '🇪🇸', nameKey: 'spain' },
  { code: 'IT', name: 'Italia', flag: '🇮🇹', nameKey: 'italy' },
  { code: 'FR', name: 'France', flag: '🇫🇷', nameKey: 'france' },
  { code: 'PL', name: 'Polska', flag: '🇵🇱', nameKey: 'poland' },
];

// Provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getCurrentLanguage());
  const [searchCountry, setSearchCountryState] = useState(getCurrentSearchCountry());
  const [isSyncing, setIsSyncing] = useState(false);

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('baremc_token');
  };

  // Fetch preferences from server on mount (if logged in)
  useEffect(() => {
    const fetchPreferences = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const response = await axios.get(`${API_URL}/users/me/preferences`);
        
        const { preferred_language, preferred_search_country } = response.data;
        
        // Update local state and localStorage with server preferences
        if (preferred_language && preferred_language !== language) {
          setLang(preferred_language);
          setLanguageState(preferred_language);
        }
        if (preferred_search_country && preferred_search_country !== searchCountry) {
          setCountry(preferred_search_country);
          setSearchCountryState(preferred_search_country);
        }
      } catch (error) {
        // Silently fail - use local preferences
        console.log('Could not fetch preferences from server');
      }
    };

    fetchPreferences();
  }, []); // Only run on mount

  // Listen for external changes
  useEffect(() => {
    const handleLanguageChange = (e) => setLanguageState(e.detail);
    const handleCountryChange = (e) => setSearchCountryState(e.detail);
    
    window.addEventListener('languageChange', handleLanguageChange);
    window.addEventListener('searchCountryChange', handleCountryChange);
    
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
      window.removeEventListener('searchCountryChange', handleCountryChange);
    };
  }, []);

  // Sync preference to server
  const syncToServer = useCallback(async (prefs) => {
    const token = getAuthToken();
    if (!token || isSyncing) return;

    setIsSyncing(true);
    try {
      await axios.put(`${API_URL}/users/me/preferences`, prefs);
    } catch (error) {
      console.log('Could not sync preferences to server');
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing]);

  // Set language (local + server)
  const setLanguage = useCallback((code) => {
    setLang(code);
    setLanguageState(code);
    // Sync to server in background
    syncToServer({ preferred_language: code });
  }, [syncToServer]);

  // Set search country (local + server)
  const setSearchCountry = useCallback((code) => {
    setCountry(code);
    setSearchCountryState(code);
    // Sync to server in background
    syncToServer({ preferred_search_country: code });
  }, [syncToServer]);

  // Translation function
  // Fallback chain: current language → English → key itself.
  // Previous version fell back to Danish which caused e.g. German to show
  // "Opret en profil" whenever landing.createProfile was missing in de.json.
  const t = useCallback((key) => {
    const keys = key.split('.');

    // Helper to look up a nested key path in a locale object
    const lookup = (locale) => {
      let value = translations[locale];
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return undefined;
        }
      }
      return typeof value === 'string' ? value : undefined;
    };

    // 1. Current language
    const current = lookup(language);
    if (current !== undefined) return current;

    // 2. English fallback
    const english = lookup('en');
    if (english !== undefined) return english;

    // 3. Return the key itself (never fall back to Danish for non-DA languages)
    return key;
  }, [language]);

  const value = {
    language,
    searchCountry,
    setLanguage,
    setSearchCountry,
    t,
    languages: LANGUAGES,
    searchCountries: SEARCH_COUNTRIES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
